import { useEffect, useState } from 'react';
import { Play, Loader2, Clock, AlertTriangle } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import ReconSection from './ReconSection';
import FileUploadField from './FileUploadField';
import HelpTip from './HelpTip';
import FilterToolbar, { FilterForm } from './FilterToolbar';

export default function TimeRecon() {
  const [g2File, setG2File] = useState(null);
  const [registerFile, setRegisterFile] = useState(null);
  const [docsInputs, setDocsInputs] = useState(null);
  const [payPeriod, setPayPeriod] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [filterDraft, setFilterDraft] = useState('');
  const [filter, setFilter] = useState('');
  const [cat, setCat] = useState('');

  useEffect(() => {
    apiFetch('/api/docs/inputs')
      .then(setDocsInputs)
      .catch(() => setDocsInputs(null));
  }, []);

  const run = async () => {
    setError('');
    setResult(null);
    setRunning(true);
    try {
      if (!g2File && !docsInputs?.g2TimeFile) {
        throw new Error('Upload the G2 time file or use the saved file from Reports.');
      }
      if (!registerFile && !docsInputs?.payrollRegister) {
        throw new Error('Upload the payroll register or use the saved file from Reports.');
      }
      const fd = new FormData();
      fd.append('g2File', g2File);
      fd.append('registerFile', registerFile);
      if (payPeriod) fd.append('payPeriod', payPeriod);
      const base = getApiBase();
      const res = await fetch(`${base}/api/time-recon/run`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Time recon failed');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const summary = result?.summary;
  const rows = result?.exceptions || [];
  const needle = filter.trim().toLowerCase();
  const cats = summary?.exceptionsByCategory ? Object.keys(summary.exceptionsByCategory) : [];
  const visible = rows.filter((r) => {
    if (cat && r.category !== cat) return false;
    if (!needle) return true;
    return [r.personNumber, r.wageType, r.detail, r.category]
      .map((v) => String(v ?? '').toLowerCase())
      .some((v) => v.includes(needle));
  });

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Pre-payroll validation</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">Time &amp; labor recon</h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">
          Compare the ADP G2 time file (hours sent) against the payroll register (hours paid) by
          person and wage type. Flags missing wage types and hour differences.{' '}
          <HelpTip term="g2" />
        </p>
      </header>

      <ReconSection
        title="Sources & run"
        headerCenter={
          <button type="button" onClick={run} disabled={running} className="btn-primary">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run time recon
          </button>
        }
      >
        <div className="grid gap-3">
          <FileUploadField
            label="ADP G2 time file"
            hint="G2 time export — or use the saved file from Reports"
            accept=".txt,.dat"
            file={g2File}
            onChange={setG2File}
            savedFile={docsInputs?.g2TimeFile}
            helpTerm="g2"
            HelpTipComponent={HelpTip}
          />
          <FileUploadField
            label="ADP payroll register"
            hint="Register with Number/Hours column"
            accept=".xlsx,.xls"
            file={registerFile}
            onChange={setRegisterFile}
            savedFile={docsInputs?.payrollRegister}
            helpTerm="register"
            HelpTipComponent={HelpTip}
          />
          <label className="text-sm">
            <span className="mb-1 block font-medium text-slate-200">Pay period (optional)</span>
            <input
              type="text"
              placeholder="e.g. 202606"
              value={payPeriod}
              onChange={(e) => setPayPeriod(e.target.value)}
              className="w-40 rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200"
            />
          </label>
        </div>
        {error && <div className="alert-error mt-4">{error}</div>}
      </ReconSection>

      {summary && (
        <>
          <div className="flex flex-wrap gap-2">
            <span className="badge-neutral inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              G2 {summary.g2Lines?.toLocaleString()} lines · register {summary.registerRows?.toLocaleString()} rows
            </span>
            <span className="badge-success">{summary.matchedCount?.toLocaleString()} matched</span>
            <span className="badge-warning inline-flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              {summary.exceptionCount?.toLocaleString()} exceptions
            </span>
          </div>

          {(summary.missingWageTypesInRegister?.length > 0 || summary.missingWageTypesInG2?.length > 0) && (
            <div className="card-panel p-4 text-xs text-slate-400">
              {summary.missingWageTypesInRegister?.length > 0 && (
                <p>
                  <span className="font-medium text-slate-300">In G2 but not on register:</span>{' '}
                  <span className="font-mono">{summary.missingWageTypesInRegister.join(', ')}</span>
                </p>
              )}
              {summary.missingWageTypesInG2?.length > 0 && (
                <p className="mt-1">
                  <span className="font-medium text-slate-300">On register but not in G2:</span>{' '}
                  <span className="font-mono">{summary.missingWageTypesInG2.join(', ')}</span>
                </p>
              )}
            </div>
          )}

          {cats.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCat(cat === c ? '' : c)}
                  className={cat === c ? 'badge-warning' : 'badge-neutral'}
                >
                  {c}: {summary.exceptionsByCategory[c].toLocaleString()}
                </button>
              ))}
            </div>
          )}

          <div className="card-panel overflow-hidden">
            <div className="section-header flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-100">Exceptions</h3>
              <FilterForm onApply={() => setFilter(filterDraft)}>
                <FilterToolbar
                  onClear={() => {
                    setFilterDraft('');
                    setFilter('');
                  }}
                  hasActiveFilters={!!filter.trim()}
                >
                  <input
                    type="search"
                    placeholder="Person, wage type…"
                    value={filterDraft}
                    onChange={(e) => setFilterDraft(e.target.value)}
                    className="filter-input min-w-[12rem] text-sm"
                  />
                </FilterToolbar>
              </FilterForm>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Wage type</th>
                    <th>Issue</th>
                    <th className="text-right">G2 hrs</th>
                    <th className="text-right">Register hrs</th>
                    <th className="text-right">Diff</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-6 text-center text-slate-500">No exceptions match the filter.</td>
                    </tr>
                  ) : (
                    visible.slice(0, 500).map((r, i) => (
                      <tr key={`${r.personNumber}-${r.wageType}-${i}`}>
                        <td className="font-mono">{r.personNumber}</td>
                        <td className="font-mono">{r.wageType}</td>
                        <td>{r.category}</td>
                        <td className="text-right tabular-nums">{r.g2Hours}</td>
                        <td className="text-right tabular-nums">{r.registerHours}</td>
                        <td className="text-right tabular-nums">{r.difference}</td>
                        <td className="max-w-md truncate text-slate-400" title={r.detail}>{r.detail}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
